import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { activityKindLabels, leadCategoryLabels } from '@schwarz/contract';
import type { ActivityItem, KpiTrend, LeadBreakdownRow } from '@schwarz/contract';

import {
  DASHBOARD_BUSY_POLL_MS,
  DASHBOARD_IDLE_POLL_MS,
  useGetDashboardQuery,
  useGetUsageQuery,
} from '../../store/dashboard-api.js';
import { messageOf } from '../../store/error-message.js';
import { PlanetSpinner } from '../../ui/BrandLogo.js';
import { IconDrop, IconHeart, IconPhone, IconTrend } from '../../ui/icons.js';
import { ago } from '../../ui/ago.js';
import styles from './DashboardPage.module.css';

/**
 * Dashboard (S11).
 *
 * Three KPI cards, the activity feed and the lead breakdown, as the
 * change-request mock lays them out — the fourth card went with the follow-up
 * email feature (D2).
 *
 * ## Why the trend line is a sentence and not an arrow with a number
 *
 * Because the honest version needs words. The mock hardcodes "▲ 12% vs
 * yesterday"; computing it means facing the calling window, since Schwarz calls
 * Tuesday to Thursday and "yesterday" is usually a day on which no call could
 * have been placed. The server sends what it compared against, and the card says
 * it — "vs Thursday" — so a figure nobody can reproduce never appears.
 *
 * The other case the words carry: a percentage of zero. On a first day, or after
 * a quiet week, `changePct` is null and the card reads "up from none" rather than
 * claiming an infinite rise or a flat zero.
 */
export function DashboardPage(): JSX.Element {
  // Polled, unlike the contacts table, which polls only while something could
  // change unattended. Here that condition is always true: a call resolving, an
  // analysis landing by webhook and a threshold being crossed all happen
  // server-side with nothing to tell the browser. Faster while something is
  // actually in flight, because that is when a figure is about to move.
  const [pollMs, setPollMs] = useState<number>(DASHBOARD_IDLE_POLL_MS);

  const { data, isLoading, isError, error, refetch } = useGetDashboardQuery(undefined, {
    pollingInterval: pollMs,
  });
  const { data: usage } = useGetUsageQuery();

  useEffect(() => {
    if (data === undefined) return;

    const busy = data.callsInFlight > 0 || data.queued > 0;
    setPollMs(busy ? DASHBOARD_BUSY_POLL_MS : DASHBOARD_IDLE_POLL_MS);
  }, [data]);

  if (isLoading) {
    return (
      <section className={styles.view}>
        <div className={styles.state}>
          <span className={styles.loading}>
            <PlanetSpinner maskId="ps-mark-dashboard" />
            Loading the dashboard…
          </span>
        </div>
      </section>
    );
  }

  if (isError || data === undefined) {
    return (
      <section className={styles.view}>
        <div className={styles.state}>
          <p>{messageOf(error)}</p>
          <button type="button" className={styles.retry} onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.view}>
      <div className={styles.stats}>
        <Stat
          icon={<IconPhone />}
          label="Calls today"
          trend={data.callsToday}
          suffix={`vs ${data.callsToday.comparedTo}`}
        />
        <Stat
          icon={<IconHeart />}
          label="Hot leads"
          trend={data.hotLeads}
          suffix={`vs ${data.hotLeads.comparedTo}`}
        />
        <Stat
          icon={<IconDrop />}
          label="Warm leads"
          trend={data.warmLeads}
          suffix={`vs ${data.warmLeads.comparedTo}`}
        />
      </div>

      {/* Only when there is something in flight. A row of zeros telling an
          operator that nothing is happening is noise on a quiet afternoon. */}
      {(data.callsInFlight > 0 || data.queued > 0) && (
        <div className={styles.now}>
          <span className={styles.pulse} aria-hidden="true" />
          {data.callsInFlight > 0 && (
            <span>
              <b>{data.callsInFlight}</b> call{data.callsInFlight === 1 ? '' : 's'} in progress
            </span>
          )}
          {data.callsInFlight > 0 && data.queued > 0 && <span aria-hidden="true">·</span>}
          {data.queued > 0 && (
            <span>
              <b>{data.queued}</b> waiting in the queue
            </span>
          )}
          <Link className={styles.nowLink} to="/contacts">
            Open the queue
          </Link>
        </div>
      )}

      <div className={styles.grid}>
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <h3>Recent activity</h3>
            <span className={styles.sub}>newest first</span>
          </div>

          {data.activity.length === 0 ? (
            <div className={styles.empty}>
              <p>Nothing has happened yet. Calls, analyses and agent changes appear here.</p>
            </div>
          ) : (
            <ul className={styles.feed}>
              {data.activity.map((item) => (
                <FeedRow key={item.id} item={item} />
              ))}
            </ul>
          )}
        </div>

        <div className={styles.side}>
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <h3>Lead categories</h3>
              <span className={styles.sub}>last 7 days</span>
            </div>
            <div className={styles.bars}>
              {data.leadBreakdown
                .filter((row) => row.category !== 'UNKNOWN' || row.count > 0)
                .map((row) => (
                  <Bar key={row.category} row={row} rows={data.leadBreakdown} />
                ))}
            </div>
          </div>

          {usage !== undefined && (
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <h3>Voice minutes</h3>
                <span className={styles.sub}>{usage.period}</span>
              </div>
              <div className={styles.usage}>
                <div className={styles.usageFigure}>
                  <b>{usage.voiceMinutesUsed.toLocaleString('en-GB')}</b>
                  <span>
                    of {usage.voiceMinutesBudget.toLocaleString('en-GB')} minutes
                    {usage.pctUsed !== null && ` · ${String(usage.pctUsed)}%`}
                  </span>
                </div>
                <div className={styles.track} aria-hidden="true">
                  <span
                    className={`${styles.fill} ${styles[usage.severity.toLowerCase()] ?? ''}`}
                    style={{ width: `${String(Math.min(100, usage.pctUsed ?? 0))}%` }}
                  />
                </div>
                <div className={styles.usageFoot}>
                  {usage.projectedExhaustion !== null && (
                    <span className={styles.warnText}>
                      At this pace the budget runs out around{' '}
                      {new Date(usage.projectedExhaustion).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                      })}
                      .
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

interface StatProps {
  icon: JSX.Element;
  label: string;
  trend: KpiTrend;
  suffix: string;
}

function Stat({ icon, label, trend, suffix }: StatProps): JSX.Element {
  const direction =
    trend.changePct === null
      ? 'flat'
      : trend.changePct > 0
        ? 'up'
        : trend.changePct < 0
          ? 'down'
          : 'flat';

  return (
    <div className={styles.stat}>
      <div className={styles.statLabel}>
        {icon}
        {label}
      </div>
      <div className={styles.statValue}>{trend.value.toLocaleString('en-GB')}</div>
      <div className={`${styles.statTrend} ${styles[direction] ?? ''}`}>
        {trend.changePct === null ? (
          // No percentage exists, so the card says what happened instead of
          // rendering a number that would be wrong in both directions.
          <span>{trend.value === 0 ? `none, and none ${suffix}` : `up from none ${suffix}`}</span>
        ) : (
          <span>
            <IconTrend width="13" height="13" />
            {trend.changePct > 0 ? '+' : ''}
            {String(trend.changePct)}% {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function Bar({
  row,
  rows,
}: {
  row: LeadBreakdownRow;
  rows: readonly LeadBreakdownRow[];
}): JSX.Element {
  const max = Math.max(...rows.map((entry) => entry.count), 1);
  const tone = row.category.toLowerCase();

  return (
    <div className={styles.bar}>
      <div className={styles.barHead}>
        <span>{leadCategoryLabels[row.category]}</span>
        <b>{row.count}</b>
      </div>
      <div className={styles.barTrack}>
        {/* The mock distinguishes these by colour alone. The pattern class adds a
            second signal, so the breakdown is still readable to somebody who
            cannot separate the greens from the ambers (S11's ask). */}
        <span
          className={`${styles.barFill} ${styles[tone] ?? ''}`}
          style={{ width: `${String((row.count / max) * 100)}%` }}
        />
      </div>
    </div>
  );
}

function FeedRow({ item }: { item: ActivityItem }): JSX.Element {
  const moment = ago(item.at);

  const body = (
    <>
      <span className={styles.who}>{item.subject}</span> {activityKindLabels[item.kind]}
      {item.detail !== null && <span className={styles.detail}> · {item.detail}</span>}
    </>
  );

  const href =
    item.linkId === null
      ? null
      : item.linkType === 'CONTACT'
        ? '/contacts'
        : item.linkType === 'INTERACTION'
          ? '/interactions'
          : item.linkType === 'AGENT'
            ? `/agents/${item.linkId}`
            : null;

  return (
    <li className={styles.feedItem}>
      <span
        className={`${styles.dot} ${styles[item.kind.toLowerCase()] ?? ''}`}
        aria-hidden="true"
      />
      <div className={styles.feedBody}>{href === null ? body : <Link to={href}>{body}</Link>}</div>
      {/* Elapsed time for today, "Yesterday" for yesterday, a date before that —
          and the exact moment on hover, because a relative label reads well and
          cannot be reasoned from. */}
      <span className={styles.feedTime} title={moment.title}>
        {moment.text}
      </span>
    </li>
  );
}
